import TableHeader from '@/components/table/tableHeader';
import { ISort } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';

import client from '@/apollo-client';
import { GET_INVESTOR_LEGAL_ENTITIES_IDS_QUERY } from '@/components/investors-page/queries';
import { Loader } from '@/components/loaders/loader';
import { StickyTableLoader } from '@/components/loaders/stickyTableLoader';
import { TableBodyWrapperWithLoading, TableWrapper } from '@/components/table/styles';
import { useNavigate } from 'react-router-dom';
import { columns } from '../constants';
import { AllocatorPageMode, ExtendedBulkAllocation } from '../types';
import MobileTable from './mobileTable';
import TableBody from './tableBody';

interface TableProps {
  sort: ISort;
  bulkAllocations: ExtendedBulkAllocation[];
  loading: boolean;
  mode: AllocatorPageMode;
  investmentName: string;
  forceRecalculateTableFiltersHeight: any;
  setSort: (value: ISort) => void;
  handleChangeMode: (mode: AllocatorPageMode) => void;
  selectAllRows: (isChecked: boolean) => void;
  selectRow: (row: ExtendedBulkAllocation) => void;
  openModalWindow: (type: string, row: any) => void;
  updateDecisionReason: (id: string, value: string) => void;
  openMobileModal: (data: ExtendedBulkAllocation, modalType: string) => void;
  preservePageState: () => void;
}

const Table = ({
  sort,
  bulkAllocations,
  loading,
  mode,
  investmentName,
  forceRecalculateTableFiltersHeight,
  setSort,
  handleChangeMode,
  selectAllRows,
  selectRow,
  openModalWindow,
  updateDecisionReason,
  openMobileModal,
  preservePageState
}: TableProps) => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  const filteredBulkAllocations = mode === AllocatorPageMode.EDIT ? bulkAllocations.filter((row) => row.editable) : bulkAllocations;

  const openInvestorDetailsPage = async ({ investorId, entityId, isGroup }: { investorId: string; entityId?: string; isGroup?: boolean }) => {
    try {
      if (entityId) {
        preservePageState();
        navigate(`/investors/${investorId}/${entityId}?isGroup=${isGroup}&backToTitle=${investmentName || 'Allocator'}`);
        return;
      }

      const { data } = await client.query({
        query: GET_INVESTOR_LEGAL_ENTITIES_IDS_QUERY,
        variables: {
          data: {
            id: investorId
          }
        }
      });

      const { legalEntities } = data.getInvestor;

      if (legalEntities?.length === 1) {
        preservePageState();
        const entity = legalEntities?.[0];
        navigate(`/investors/${investorId}/${entity?.id}?isGroup=${entity?.isGroup}&backToTitle=${investmentName || 'Allocator'}`);
        return;
      }

      preservePageState();
      navigate(`/investors/${investorId}?backToTitle=${investmentName || 'Allocator'}`);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <TableWrapper padding="0">
      {isMobile ? (
        <>
          {loading ? (
            <Loader />
          ) : (
            <MobileTable bulkAllocations={bulkAllocations} openMobileModal={openMobileModal} openInvestorDetailsPage={openInvestorDetailsPage} />
          )}
        </>
      ) : (
        <>
          <TableHeader
            refetch={() => {}}
            columns={columns}
            savedSort={sort}
            savedSetSort={setSort}
            checkBox={{
              checked: filteredBulkAllocations.every((row) => row.checked) && !!filteredBulkAllocations.length,
              onChange: (isChecked) => selectAllRows(isChecked),
              show: mode === AllocatorPageMode.EDIT
            }}
            forceRecalculateTableFiltersHeight={forceRecalculateTableFiltersHeight}
          />
          {loading && <StickyTableLoader forceRecalculateTopPosition={forceRecalculateTableFiltersHeight} />}
          <TableBodyWrapperWithLoading isLoading={loading}>
            <TableBody
              bulkAllocations={filteredBulkAllocations}
              mode={mode}
              selectRow={selectRow}
              openModalWindow={openModalWindow}
              handleChangeMode={handleChangeMode}
              updateDecisionReason={updateDecisionReason}
              openInvestorDetailsPage={openInvestorDetailsPage}
            />
          </TableBodyWrapperWithLoading>
        </>
      )}
    </TableWrapper>
  );
};

export default Table;
